<script setup>
import { computed } from 'vue'
import { BadgeCheck, ShieldCheck, BookOpen, Video, Layers } from 'lucide-vue-next'

/**
 * Facts about this tutor, from this tutor's record.
 *
 * This replaces a badge panel that told every visitor the same three things —
 * "Top Rated · in top 5% of tutors" and "Qualified Teacher · QTS certified" —
 * regardless of who they were looking at. Neither was measured anywhere, and
 * both appeared on profiles of tutors who were neither.
 *
 * A row only appears when there is something true to put in it.
 */
const props = defineProps({
  tutor: { type: Object, required: true },
})

const highlights = computed(() => {
  const rows = []

  if (props.tutor.is_verified) {
    rows.push({
      icon: BadgeCheck,
      title: 'Verified',
      body: 'Identity, degree and grades checked by Bindle',
    })
  }

  rows.push({
    icon: ShieldCheck,
    title: 'DBS checked',
    body: 'Required before any tutor can be listed',
  })

  if (props.tutor.levels?.length) {
    rows.push({
      icon: Layers,
      title: 'Teaches',
      body: props.tutor.levels.map((level) => level.name).join(' · '),
    })
  }

  if (props.tutor.examboards?.length) {
    rows.push({
      icon: BookOpen,
      title: 'Exam boards',
      body: props.tutor.examboards.map((board) => board.name).join(' · '),
    })
  }

  if (props.tutor.videos_count > 0) {
    rows.push({
      icon: Video,
      title: 'Video content',
      body: `${props.tutor.videos_count} ${props.tutor.videos_count === 1 ? 'lesson' : 'lessons'} published`,
    })
  }

  return rows
})
</script>

<template>
  <section class="surface-card p-6">
    <h2 class="font-sans text-base font-bold text-ink">Tutor highlights</h2>
    <ul class="mt-4 space-y-4">
      <li v-for="row in highlights" :key="row.title" class="flex gap-3.5">
        <span class="grid size-9 shrink-0 place-items-center rounded-full bg-brand-soft text-brand">
          <component :is="row.icon" class="size-4.5" />
        </span>
        <div class="min-w-0">
          <p class="text-sm font-semibold text-ink">{{ row.title }}</p>
          <p class="text-sm text-muted">{{ row.body }}</p>
        </div>
      </li>
    </ul>
  </section>
</template>
